import { useCallback, useEffect, useRef, useState } from 'react';
export function compareOwnersByActiveProperties(left, right, order) {
  const leftCount = left.active_property_count;
  const rightCount = right.active_property_count;
  if (typeof leftCount !== 'number' || typeof rightCount !== 'number') {
    if (typeof leftCount === 'number') return -1;
    if (typeof rightCount === 'number') return 1;
  } else if (leftCount !== rightCount) {
    return order === 'asc' ? leftCount - rightCount : rightCount - leftCount;
  }
  // Match the backend's normalized Customer ID order, including numeric IDs.
  return left.owner_key < right.owner_key ? -1 : left.owner_key > right.owner_key ? 1 : 0;
}

export const managementError = (error) =>
  error instanceof Error ? error.message : 'Unable to complete this request.';
export function useManagementResource(request, path) {
  const [data, setData] = useState();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(Boolean(path));
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    const abort = new AbortController();
    setData(undefined);
    setError('');
    setLoading(Boolean(path));
    if (!path) return () => abort.abort();
    const timer = setTimeout(
      () => {
        void request(path, 'GET', undefined, abort.signal)
          .then((value) => {
            if (!abort.signal.aborted) setData(value);
          })
          .catch((cause) => {
            if (!abort.signal.aborted) setError(managementError(cause));
          })
          .finally(() => {
            if (!abort.signal.aborted) setLoading(false);
          });
      },
      path.includes('q=') ? 200 : 0,
    );
    return () => {
      abort.abort();
      clearTimeout(timer);
    };
  }, [request, path, revision]);
  return { data, error, loading, reload };
}
const signature = (name, members, customers) =>
  JSON.stringify([
    name.trim(),
    [...members].sort((a, b) => a - b),
    Object.keys(customers).sort(),
  ]);
export function useWorkgroupEditor(request, id) {
  const detail = useManagementResource(
    request,
    id ? `/workgroups/${id}` : null,
  );
  const people = useManagementResource(request, '/users');
  const [name, setName] = useState('');
  const [members, setMembers] = useState([]);
  const [customers, setCustomers] = useState({});
  const [baseline, setBaseline] = useState(signature('', [], {}));
  const [savedName, setSavedName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const lock = useRef(false);
  const initial = useRef({ name: '', members: [], customers: {} });
  useEffect(() => {
    if (!detail.data) return;
    const value = detail.data;
    const selected = Object.fromEntries(
      value.customers.map((owner) => [owner.owner_key, owner]),
    );
    initial.current = {
      name: value.workgroup.name,
      members: value.member_user_ids,
      customers: selected,
    };
    setName(value.workgroup.name);
    setSavedName(value.workgroup.name);
    setMembers(value.member_user_ids);
    setCustomers(selected);
    setBaseline(
      signature(value.workgroup.name, value.member_user_ids, selected),
    );
    setError('');
  }, [detail.data]);
  const reset = () => {
    setName(initial.current.name);
    setMembers(initial.current.members);
    setCustomers(initial.current.customers);
    setError('');
  };
  const updateCustomerCounts = useCallback((owners) => {
    setCustomers((current) => {
      const changed = owners.filter((owner) => current[owner.owner_key]
        && current[owner.owner_key].active_property_count !== owner.active_property_count);
      if (!changed.length) return current;
      const next = { ...current };
      for (const owner of changed) {
        next[owner.owner_key] = {
          ...next[owner.owner_key],
          active_property_count: owner.active_property_count,
        };
      }
      return next;
    });
  }, []);
  const toggleCustomer = (owner) =>
    setCustomers((current) => {
      const next = { ...current };
      if (next[owner.owner_key]) delete next[owner.owner_key];
      else next[owner.owner_key] = owner;
      return next;
    });
  const selectCustomers = (owners, checked) =>
    setCustomers((current) => {
      const next = { ...current };
      for (const owner of owners) {
        if (checked) next[owner.owner_key] = owner;
        else delete next[owner.owner_key];
      }
      return next;
    });
  const toggleMember = (userID) =>
    setMembers((current) =>
      current.includes(userID)
        ? current.filter((value) => value !== userID)
        : [...current, userID],
    );
  const save = async () => {
    if (
      lock.current ||
      !name.trim() ||
      detail.loading ||
      people.loading ||
      detail.error ||
      people.error
    )
      return undefined;
    lock.current = true;
    setSaving(true);
    setError('');
    try {
      const result = await request(
        id ? `/workgroups/${id}` : '/workgroups',
        id ? 'PUT' : 'POST',
        {
          name: name.trim(),
          member_user_ids: members,
          customer_owner_keys: Object.keys(customers),
        },
      );
      const selected = Object.fromEntries(
        Object.values(customers).map((owner) => [
          owner.owner_key,
          {
            ...owner,
            workgroup_id: result.workgroup.id,
            workgroup_name: result.workgroup.name,
          },
        ]),
      );
      initial.current = {
        name: result.workgroup.name,
        members: [...members],
        customers: selected,
      };
      setName(result.workgroup.name);
      setSavedName(result.workgroup.name);
      setCustomers(selected);
      setBaseline(signature(result.workgroup.name, members, selected));
      return result.workgroup;
    } catch (cause) {
      setError(managementError(cause));
      return undefined;
    } finally {
      lock.current = false;
      setSaving(false);
    }
  };
  return {
    name,
    setName,
    members,
    customers,
    savedName,
    updateCustomerCounts,
    error,
    saving,
    detail,
    people,
    reset,
    toggleCustomer,
    selectCustomers,
    toggleMember,
    save,
    dirty: signature(name, members, customers) !== baseline,
    ready: !detail.loading && !people.loading && !detail.error && !people.error,
  };
}
